import { useEffect, useMemo, useRef, useState } from 'react'
import { X, Plus, Trash2, AlertTriangle, CheckCircle2, Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useParentTree,
  useCreatePortfolioDraft,
  type BuilderRole,
  type BuilderKeyword,
  type BuilderPatTarget,
  type PortfolioBuilderResult,
} from '@/api/portfolios'

interface PortfolioBuilderWizardProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  parentId: number | null
  /** Vorbefuellte Werte aus Legacy-Migration. Optional. */
  presetFromLegacy?: {
    role?: BuilderRole
    adType?: 'sp' | 'sb' | 'sd'
    budget?: number
    childAsins?: string[]
    seedKeywords?: BuilderKeyword[]
    biddingStrategy?: 'dynamic_bids_down_only' | 'dynamic_bids_up_and_down' | 'fixed_bid'
  }
}

type Step = 1 | 2 | 3 | 4

const DEFAULT_BUDGET = 10
const DEFAULT_START_BID = 0.3
const MAX_KEYWORDS_WARNING = 5
const DEFAULT_MATCH_BY_ROLE: Record<BuilderRole, 'broad' | 'phrase' | 'exact'> = {
  broad: 'broad',
  exact: 'exact',
  pat: 'exact',
  auto: 'exact',
}

export default function PortfolioBuilderWizard({
  open,
  onOpenChange,
  parentId,
  presetFromLegacy,
}: PortfolioBuilderWizardProps) {
  const { data: tree } = useParentTree(open ? parentId : null)
  const createDraft = useCreatePortfolioDraft()
  const dialogRef = useRef<HTMLDivElement>(null)

  // Step 1 state
  const [step, setStep] = useState<Step>(1)
  const [role, setRole] = useState<BuilderRole>(presetFromLegacy?.role ?? 'exact')
  const [adType, setAdType] = useState<'sp' | 'sb' | 'sd'>(presetFromLegacy?.adType ?? 'sp')
  const [budget, setBudget] = useState<number>(presetFromLegacy?.budget ?? DEFAULT_BUDGET)
  const [biddingStrategy, setBiddingStrategy] = useState<
    'dynamic_bids_down_only' | 'dynamic_bids_up_and_down' | 'fixed_bid' | ''
  >(presetFromLegacy?.biddingStrategy ?? 'dynamic_bids_down_only')
  const [parentShortOverride, setParentShortOverride] = useState<string>('')

  // Step 2
  const [selectedChildAsins, setSelectedChildAsins] = useState<string[]>(
    presetFromLegacy?.childAsins ?? [],
  )

  // Step 3
  const [keywords, setKeywords] = useState<BuilderKeyword[]>(presetFromLegacy?.seedKeywords ?? [])
  const [keywordInput, setKeywordInput] = useState('')
  const [patTargets, setPatTargets] = useState<BuilderPatTarget[]>([])
  const [patInput, setPatInput] = useState('')
  const [startBidDefault, setStartBidDefault] = useState<number>(DEFAULT_START_BID)

  // Step 4
  const [result, setResult] = useState<PortfolioBuilderResult | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset beim Oeffnen / beim Parent-Wechsel
  useEffect(() => {
    if (open) {
      setStep(1)
      setRole(presetFromLegacy?.role ?? 'exact')
      setAdType(presetFromLegacy?.adType ?? 'sp')
      setBudget(presetFromLegacy?.budget ?? DEFAULT_BUDGET)
      setBiddingStrategy(presetFromLegacy?.biddingStrategy ?? 'dynamic_bids_down_only')
      setParentShortOverride('')
      setSelectedChildAsins(presetFromLegacy?.childAsins ?? [])
      setKeywords(presetFromLegacy?.seedKeywords ?? [])
      setKeywordInput('')
      setPatTargets([])
      setPatInput('')
      setStartBidDefault(DEFAULT_START_BID)
      setResult(null)
      setSubmitError(null)
    }
  }, [open, parentId, presetFromLegacy])

  // ESC schliesst
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [open, onOpenChange])

  // Alle Children des Parents, aggregiert aus dem Tree
  const allChildrenAsins = useMemo(() => {
    if (!tree) return []
    return tree.children.map((c) => c.amazonChildAsin).filter((a) => /^B0[A-Z0-9]{8}$/.test(a))
  }, [tree])

  // Bei leerem Children-Set im Preset: alle Children autoselektieren
  useEffect(() => {
    if (open && selectedChildAsins.length === 0 && allChildrenAsins.length > 0) {
      setSelectedChildAsins(allChildrenAsins)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allChildrenAsins.length])

  const keywordOverWarning = keywords.length > MAX_KEYWORDS_WARNING
  // Duplikat-Check macht das Backend serverseitig — Warnings kommen in result.warnings zurueck

  if (!open) return null

  function addKeyword() {
    const t = keywordInput.trim()
    if (!t) return
    if (keywords.some((k) => k.text.toLowerCase() === t.toLowerCase())) {
      setKeywordInput('')
      return
    }
    setKeywords([...keywords, { text: t, matchType: DEFAULT_MATCH_BY_ROLE[role] }])
    setKeywordInput('')
  }

  function addPatTarget() {
    const raw = patInput.trim().toUpperCase()
    if (!/^B0[A-Z0-9]{8}$/.test(raw)) return
    if (patTargets.some((p) => p.asin === raw)) {
      setPatInput('')
      return
    }
    setPatTargets([...patTargets, { asin: raw }])
    setPatInput('')
  }

  async function handleSubmit() {
    if (!parentId) {
      setSubmitError('Kein Parent gewaehlt')
      return
    }
    if (selectedChildAsins.length === 0) {
      setSubmitError('Mindestens eine Child-ASIN muss gewaehlt sein')
      return
    }
    setSubmitError(null)
    try {
      const res = await createDraft.mutateAsync({
        parentId,
        role,
        adType,
        budget,
        biddingStrategy: biddingStrategy || undefined,
        childAsins: selectedChildAsins,
        seedKeywords: keywords.length > 0 ? keywords : undefined,
        patTargets: patTargets.length > 0 ? patTargets : undefined,
        parentShortOverride: parentShortOverride || undefined,
        startBidDefault,
      })
      setResult(res.draft)
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Unbekannter Fehler')
    }
  }

  const canGoNext =
    (step === 1 && budget > 0) ||
    (step === 2 && selectedChildAsins.length > 0) ||
    (step === 3 && (role === 'auto' || keywords.length > 0 || patTargets.length > 0)) ||
    step === 4

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={() => onOpenChange(false)}
    >
      <div
        ref={dialogRef}
        className="flex h-[min(700px,90vh)] w-full max-w-3xl flex-col rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
          <div>
            <h2 className="text-base font-semibold">Neue Portfolio-Kampagne</h2>
            {tree?.parent && (
              <p className="text-xs text-[var(--muted-foreground)]">
                Parent:{' '}
                <span className="font-mono">{tree.parent.amazonParentAsin}</span>
                {tree.parent.ppcCode && (
                  <>
                    {' '}
                    · <span>{tree.parent.ppcCode}</span>
                  </>
                )}{' '}
                · {tree.parent.name}
              </p>
            )}
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="rounded-md p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
            aria-label="Schliessen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 border-b border-[var(--border)] bg-[var(--muted)]/40 px-4 py-2">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={cn(
                'flex items-center gap-1 text-xs',
                s === step ? 'font-semibold text-[var(--foreground)]' : 'text-[var(--muted-foreground)]',
              )}
            >
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[10px]',
                  s === step
                    ? 'bg-zinc-900 text-white'
                    : s < step
                      ? 'bg-emerald-500 text-white'
                      : 'bg-zinc-200 text-zinc-600',
                )}
              >
                {s < step ? '✓' : s}
              </span>
              <span>
                {s === 1 && 'Einstellungen'}
                {s === 2 && 'Children'}
                {s === 3 && 'Keywords/Targets'}
                {s === 4 && 'Preview'}
              </span>
              {s < 4 && <span className="mx-1 text-[var(--muted-foreground)]">›</span>}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {/* --- Step 1 --- */}
          {step === 1 && (
            <div className="space-y-4">
              <FieldRow label="Rolle">
                <div className="flex gap-2">
                  {(['broad', 'exact', 'pat', 'auto'] as BuilderRole[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      className={cn(
                        'rounded-md border px-3 py-1.5 text-sm capitalize transition-colors',
                        r === role
                          ? 'border-zinc-900 bg-zinc-900 text-white'
                          : 'border-[var(--border)] bg-[var(--background)] hover:bg-[var(--muted)]',
                      )}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </FieldRow>

              <FieldRow label="Ad-Type">
                <div className="flex gap-2">
                  {(['sp', 'sb', 'sd'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setAdType(t)}
                      className={cn(
                        'rounded-md border px-3 py-1.5 text-sm uppercase transition-colors',
                        t === adType
                          ? 'border-zinc-900 bg-zinc-900 text-white'
                          : 'border-[var(--border)] bg-[var(--background)] hover:bg-[var(--muted)]',
                      )}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </FieldRow>

              <FieldRow label="Budget (EUR / Tag)">
                <input
                  type="number"
                  min={1}
                  max={10000}
                  step={0.5}
                  value={budget}
                  onChange={(e) => setBudget(parseFloat(e.target.value) || 0)}
                  className="h-9 w-32 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                />
              </FieldRow>

              <FieldRow label="Bidding-Strategie">
                <select
                  value={biddingStrategy}
                  onChange={(e) =>
                    setBiddingStrategy(
                      e.target.value as
                        | 'dynamic_bids_down_only'
                        | 'dynamic_bids_up_and_down'
                        | 'fixed_bid'
                        | '',
                    )
                  }
                  className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                >
                  <option value="">(Amazon-Default)</option>
                  <option value="dynamic_bids_down_only">Dynamic bids — down only</option>
                  <option value="dynamic_bids_up_and_down">Dynamic bids — up &amp; down</option>
                  <option value="fixed_bid">Fixed bid</option>
                </select>
              </FieldRow>

              <FieldRow label="Parent-Kurzname (optional Override)">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    value={parentShortOverride}
                    onChange={(e) => setParentShortOverride(e.target.value.toUpperCase())}
                    placeholder={deriveParentShortPreview(tree?.parent?.name ?? '')}
                    className="h-9 w-64 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 font-mono text-xs outline-none focus:ring-2 focus:ring-[var(--ring)]"
                  />
                  <p className="text-[10px] text-[var(--muted-foreground)]">
                    Muster: [A-Z0-9_] 3–40 Zeichen. Leer lassen fuer automatische Ableitung.
                  </p>
                </div>
              </FieldRow>
            </div>
          )}

          {/* --- Step 2 --- */}
          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">Children-ASINs auswaehlen</h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedChildAsins(allChildrenAsins)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Alle
                  </button>
                  <button
                    onClick={() => setSelectedChildAsins([])}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Keine
                  </button>
                </div>
              </div>
              {allChildrenAsins.length === 0 ? (
                <div className="rounded-md border border-dashed border-[var(--border)] px-4 py-8 text-center text-xs text-[var(--muted-foreground)]">
                  Keine Children fuer diesen Parent im Mapping. CSV-Import erforderlich.
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-1 rounded-md border border-[var(--border)] p-2 sm:grid-cols-3">
                  {allChildrenAsins.map((asin) => (
                    <label
                      key={asin}
                      className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-[var(--muted)]"
                    >
                      <input
                        type="checkbox"
                        checked={selectedChildAsins.includes(asin)}
                        onChange={(e) => {
                          if (e.target.checked) setSelectedChildAsins([...selectedChildAsins, asin])
                          else setSelectedChildAsins(selectedChildAsins.filter((a) => a !== asin))
                        }}
                        className="rounded"
                      />
                      <span className="font-mono text-xs">{asin}</span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-[var(--muted-foreground)]">
                Mina-Empfehlung: alle Children-Varianten in jeder Kampagne (User kauft oft eine andere Variante als
                geklickt).
              </p>
            </div>
          )}

          {/* --- Step 3 --- */}
          {step === 3 && (
            <div className="space-y-5">
              {/* Keywords */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-medium">Keywords ({keywords.length})</h3>
                  {keywordOverWarning && (
                    <div className="flex items-center gap-1 text-[11px] text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      Mina-Empfehlung: max. {MAX_KEYWORDS_WARNING} Keywords
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addKeyword()
                      }
                    }}
                    placeholder="Keyword eingeben und Enter"
                    className="h-9 flex-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                  />
                  <button
                    onClick={addKeyword}
                    className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
                  >
                    <Plus className="h-3 w-3" /> Add
                  </button>
                </div>
                {keywords.length > 0 && (
                  <div className="mt-2 space-y-1 rounded-md border border-[var(--border)]">
                    {keywords.map((kw, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 border-b border-[var(--border)] px-2 py-1.5 text-sm last:border-b-0"
                      >
                        <span className="flex-1 truncate">{kw.text}</span>
                        <select
                          value={kw.matchType ?? DEFAULT_MATCH_BY_ROLE[role]}
                          onChange={(e) => {
                            const updated = [...keywords]
                            updated[idx] = {
                              ...updated[idx]!,
                              matchType: e.target.value as 'broad' | 'phrase' | 'exact',
                            }
                            setKeywords(updated)
                          }}
                          className="h-7 rounded border border-[var(--border)] bg-[var(--background)] px-1 text-xs"
                        >
                          <option value="broad">broad</option>
                          <option value="phrase">phrase</option>
                          <option value="exact">exact</option>
                        </select>
                        <input
                          type="number"
                          min={0.02}
                          max={20}
                          step={0.01}
                          value={kw.bid ?? startBidDefault}
                          onChange={(e) => {
                            const updated = [...keywords]
                            updated[idx] = {
                              ...updated[idx]!,
                              bid: parseFloat(e.target.value) || undefined,
                            }
                            setKeywords(updated)
                          }}
                          className="h-7 w-20 rounded border border-[var(--border)] bg-[var(--background)] px-2 text-xs"
                        />
                        <span className="text-[10px] text-[var(--muted-foreground)]">EUR</span>
                        <button
                          onClick={() => setKeywords(keywords.filter((_, i) => i !== idx))}
                          className="rounded p-1 text-[var(--muted-foreground)] hover:bg-red-100 hover:text-red-600"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* PAT */}
              {role === 'pat' && (
                <div>
                  <h3 className="mb-2 text-sm font-medium">Product-Targets (PAT-ASINs)</h3>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={patInput}
                      onChange={(e) => setPatInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addPatTarget()
                        }
                      }}
                      placeholder="B0XXXXXXXX"
                      className="h-9 flex-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 font-mono text-sm outline-none focus:ring-2 focus:ring-[var(--ring)]"
                    />
                    <button
                      onClick={addPatTarget}
                      className="flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
                    >
                      <Plus className="h-3 w-3" /> Add
                    </button>
                  </div>
                  {patTargets.length > 0 && (
                    <div className="mt-2 space-y-1 rounded-md border border-[var(--border)]">
                      {patTargets.map((t, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-2 border-b border-[var(--border)] px-2 py-1.5 text-sm last:border-b-0"
                        >
                          <span className="flex-1 font-mono">{t.asin}</span>
                          <input
                            type="number"
                            min={0.02}
                            max={20}
                            step={0.01}
                            value={t.bid ?? startBidDefault}
                            onChange={(e) => {
                              const updated = [...patTargets]
                              updated[idx] = {
                                ...updated[idx]!,
                                bid: parseFloat(e.target.value) || undefined,
                              }
                              setPatTargets(updated)
                            }}
                            className="h-7 w-20 rounded border border-[var(--border)] bg-[var(--background)] px-2 text-xs"
                          />
                          <span className="text-[10px] text-[var(--muted-foreground)]">EUR</span>
                          <button
                            onClick={() => setPatTargets(patTargets.filter((_, i) => i !== idx))}
                            className="rounded p-1 text-[var(--muted-foreground)] hover:bg-red-100 hover:text-red-600"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <FieldRow label="Default Start-Bid (EUR)">
                <input
                  type="number"
                  min={0.02}
                  max={20}
                  step={0.01}
                  value={startBidDefault}
                  onChange={(e) => setStartBidDefault(parseFloat(e.target.value) || 0)}
                  className="h-9 w-24 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
                />
              </FieldRow>
            </div>
          )}

          {/* --- Step 4 Preview --- */}
          {step === 4 && (
            <div className="space-y-4">
              {!result ? (
                <div className="space-y-4">
                  <div className="rounded-md border border-[var(--border)] bg-[var(--muted)]/30 px-4 py-3">
                    <div className="mb-1 text-xs uppercase tracking-wide text-[var(--muted-foreground)]">
                      Zusammenfassung
                    </div>
                    <dl className="grid grid-cols-[120px_1fr] gap-y-1 text-sm">
                      <dt className="text-[var(--muted-foreground)]">Rolle</dt>
                      <dd className="capitalize">{role}</dd>
                      <dt className="text-[var(--muted-foreground)]">Ad-Type</dt>
                      <dd className="uppercase">{adType}</dd>
                      <dt className="text-[var(--muted-foreground)]">Budget</dt>
                      <dd>{budget.toFixed(2)} EUR / Tag</dd>
                      <dt className="text-[var(--muted-foreground)]">Bidding</dt>
                      <dd>{biddingStrategy || '(Default)'}</dd>
                      <dt className="text-[var(--muted-foreground)]">Children</dt>
                      <dd>{selectedChildAsins.length} ASIN(s)</dd>
                      <dt className="text-[var(--muted-foreground)]">Keywords</dt>
                      <dd>{keywords.length}</dd>
                      {role === 'pat' && (
                        <>
                          <dt className="text-[var(--muted-foreground)]">PAT-Targets</dt>
                          <dd>{patTargets.length}</dd>
                        </>
                      )}
                    </dl>
                  </div>

                  {keywordOverWarning && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <div>
                        <strong>Mina-Warnung:</strong> {keywords.length} Keywords — empfohlen
                        maximal {MAX_KEYWORDS_WARNING}. Spend konzentriert sich sonst auf Top-Keywords.
                      </div>
                    </div>
                  )}

                  {submitError && (
                    <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {submitError}
                    </div>
                  )}

                  <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-900">
                    <Info className="mt-0.5 h-3 w-3 shrink-0" />
                    <div>
                      Nach dem Absenden wird ein <strong>portfolio_drafts</strong>-Record
                      angelegt und eine Kette von <strong>promotion_events</strong> im
                      Status <code className="font-mono">draft</code> erzeugt. Es erfolgt{' '}
                      <strong>kein Amazon-Write</strong> — der Operator muss den Draft
                      spaeter in der Approval-Queue freigeben.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                      <strong>Draft #{result.draftId} erstellt</strong> — Kampagnen-Name{' '}
                      <code className="font-mono text-xs">{result.generatedName}</code>
                    </div>
                  </div>

                  <div className="rounded-md border border-[var(--border)] bg-[var(--muted)]/30 px-4 py-3 text-xs">
                    <div className="mb-1 uppercase tracking-wide text-[var(--muted-foreground)]">
                      AdGroup
                    </div>
                    <div className="font-mono">{result.previewPayload.adGroupName}</div>
                  </div>

                  {result.previewPayload.keywords.length > 0 && (
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-[var(--muted-foreground)]">
                        Keywords ({result.previewPayload.keywords.length})
                      </div>
                      <div className="rounded-md border border-[var(--border)]">
                        {result.previewPayload.keywords.map((k, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between border-b border-[var(--border)] px-3 py-1 text-xs last:border-b-0"
                          >
                            <span>{k.text}</span>
                            <span className="flex items-center gap-2 text-[var(--muted-foreground)]">
                              <span className="uppercase">{k.matchType}</span>
                              <span>{k.bid.toFixed(2)} EUR</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {result.warnings.length > 0 && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                      <div className="mb-1 font-semibold">Backend-Warnungen:</div>
                      <ul className="list-disc pl-4">
                        {result.warnings.map((w, i) => (
                          <li key={i}>{w}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="text-[10px] text-[var(--muted-foreground)]">
                    Group-ID: <code className="font-mono">{result.groupId}</code>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 border-t border-[var(--border)] px-4 py-3">
          <div className="text-xs text-[var(--muted-foreground)]">
            Schritt {step} / 4
          </div>
          <div className="flex gap-2">
            {step > 1 && !result && (
              <button
                onClick={() => setStep((step - 1) as Step)}
                className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--muted)]"
              >
                Zurueck
              </button>
            )}
            {step < 4 && (
              <button
                onClick={() => canGoNext && setStep((step + 1) as Step)}
                disabled={!canGoNext}
                className={cn(
                  'rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-40',
                )}
              >
                Weiter
              </button>
            )}
            {step === 4 && !result && (
              <button
                onClick={handleSubmit}
                disabled={createDraft.isPending}
                className={cn(
                  'rounded-md bg-zinc-900 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-zinc-800 disabled:opacity-40',
                )}
              >
                {createDraft.isPending ? 'Erstelle Draft...' : 'Als Draft speichern'}
              </button>
            )}
            {step === 4 && result && (
              <button
                onClick={() => onOpenChange(false)}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Schliessen
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function deriveParentShortPreview(name: string): string {
  if (!name) return 'AUTO_ABLEITUNG'
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40)
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[180px_1fr] items-center gap-3">
      <label className="text-sm font-medium">{label}</label>
      <div>{children}</div>
    </div>
  )
}
